import { Box, CircularProgress, Dialog, Drawer, IconButton, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import api from '../../service/api'
import { attachmentThumbnails, convertToCDNLink, DOC_VIEWER_SUPPORTED_FORMATS, getFileSizeFromBytes, Icons, formatDate } from '../../utils/utilities'
import { toast } from 'react-toastify'
import DocumentViewer from './DocumentViewer'
import TryAgainMessageBlock from './TryAgainMessageBlock'
import DataLoadingSpinner from './DataLoadingSpinner'
import { AttachmentOutlined, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'

const betaTestAnnouncement = {
    "id": 1,
    "announcement_title": "Alive is in Beta Mode!",
    "announcement_message": "<p class=\"ql-align-justify\"> As this is a beta version, you may encounter some quirks along the way. Please bear with us as we work to make everything perfect. If you spot something that needs attention, don’t hesitate to let us know. <br><p class=\"ql-align-justify\"><span>— The Alive Team</span></p></p>",
    "attachments": [],
    "cover_image": null,
    "announcement_status": "published",
    "announcement_created_at": "2024-12-17T12:00:12.234Z",
    "announcement_updated_at": "2024-12-17T12:00:12.234Z"
}

/**
 * AttachmentChip component displays a file attachment as a chip with options to view or download.
 * Shows file type, name, mimetype, and size. Handles viewing and downloading logic for supported formats.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.attachment - The attachment object containing file details
 * @param {Object} props.announcement - The announcement object the attachment belongs to
 * @param {Function} props.setPreviewingFileDetails - Setter for the file details to preview
 * @param {Function} props.setPreviewFile - Setter for the file preview URL
 * @returns {JSX.Element}
 */
const AttachmentChip = ( { attachment, announcement, setPreviewingFileDetails, setPreviewFile } ) => {
    const [downloading, setDownloading] = useState( false )
    const [waitingForViewing, setWaitingForViewing] = useState( false )

    const { palette } = useTheme()

    const attachmentType = attachment.name?.split( "." ).pop()?.toLowerCase()

    const handleClickOnChip = () => {
        if ( DOC_VIEWER_SUPPORTED_FORMATS.includes( attachmentType ) )
            viewAttachment()
        else
            toast( "Cannot view this type of file please download and try to view it on the system." )
    }

    const viewAttachment = async () => {
        setWaitingForViewing( true )
        try {
            const { data: { data } } = await api.announcements.fetchAnnouncementAttachment( announcement.id, attachment )
            setPreviewFile( convertToCDNLink( data ) )
            setPreviewingFileDetails( attachment )
        } catch ( err ) {
            console.log( err )

            toast( err?.response?.data?.message || "Couldn't preview the file" )
        } finally {
            setWaitingForViewing( false )
        }
    }


    const downloadAttachment = async () => {
        setDownloading( true )
        try {
            const { data: { data } } = await api.announcements.fetchAnnouncementAttachment( announcement.id, attachment )
            const anchor = window.document.createElement( 'a' )
            anchor.href = convertToCDNLink( data )
            anchor.download = attachment.name
            anchor.click()
            anchor.remove()
        } catch ( err ) {
            toast( err?.response?.data?.message || "Couldn't preview the file" )
        } finally {
            setDownloading( false )
        }
    }


    return ( <Box sx={{ cursor: "pointer" }} bgcolor={palette.primary.light + 22} border={`2px solid ${palette.primary.main}`} display="flex" gap="5px" alignItems="center" borderRadius="20px" padding="2px 5px 2px 2px">
        <img width="35px" src={attachmentThumbnails[attachmentType] || attachmentThumbnails["default"]} alt={attachment.name} />
        <Box onClick={handleClickOnChip}>
            <Typography title={attachment.name} maxWidth="120px" noWrap fontSize={{ md: "14px", xs: "12px" }} fontWeight="500">{attachment.name}</Typography>
            <Box display="flex" gap="5px" alignItems="center">
                <Typography title={attachment.mimetype} display="block" marginTop="-2px" maxWidth="80px" noWrap fontSize={{ md: "12px", xs: "10px" }}><em>{attachment.mimetype}</em></Typography>
                <Typography display="inline" marginTop="-2px" fontSize={{ md: "12px", xs: "10px" }}><em>. {getFileSizeFromBytes( attachment.size )}</em></Typography>
            </Box>
        </Box>
        {DOC_VIEWER_SUPPORTED_FORMATS.includes( attachmentType ) && <IconButton onClick={() => viewAttachment( attachment )} size="small">
            {waitingForViewing ? <CircularProgress size={14} /> : Icons.default.VisibilityIcon}
        </IconButton>}
        <IconButton onClick={() => downloadAttachment( attachment )} size="small">
            {downloading ? <CircularProgress size={14} /> : Icons.default.DownloadIcon}
        </IconButton>
    </Box> )
}

/**
 * AnnouncementDialog component displays an announcement in a dialog with title, message, cover image, and attachments.
 * Handles loading, error, and previewing of attachments. Supports responsive layout and attachment viewing/downloading.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Number|String} props.announcement_id - The ID of the announcement to display
 * @param {Boolean} props.state - Whether the dialog is open
 * @param {Function} props.onClose - Callback to close the dialog
 * @returns {JSX.Element}
 */
const AnnouncementDialog = ( { announcement_id, state, onClose } ) => {

    const [previewFile, setPreviewFile] = useState( null )
    const [previewingFileDetails, setPreviewingFileDetails] = useState( null )
    const [announcement, setAnnouncement] = useState( null )
    const [showAttachmentsOnSmallScreen, setShowAttachmentsOnSmallScreen] = useState( false )
    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( null )

    const { palette, theme, border } = useTheme()

    const fetchAnnouncement = useCallback( async ( showLoading = true ) => {
        if ( showLoading )
            setLoading( true )
        if ( +announcement_id === 1 ) {
            setAnnouncement( betaTestAnnouncement )
            setError( null )
            setLoading( false )
            return
        }

        try {
            const { data: { data } } = await api.announcements.fetchAnnouncement( announcement_id )
            setAnnouncement( data )
            setError( null )
            return data
        } catch ( err ) {
            setError( { code: err?.response?.status, message: err?.response?.data?.message } )
        } finally {
            setLoading( false )
        }
    }, [announcement_id] )

    useEffect( () => {
        fetchAnnouncement()
    }, [fetchAnnouncement] )

    return (
        <Dialog className={theme} open={state} onClose={onClose} slotProps={{
            paper: { sx: { height: "80%", display: "flex", flexDirection: "column", overflow: "auto", width: "90%", maxWidth: "1200px" } }
        }}>
            {Boolean( previewingFileDetails ) && <Drawer anchor='bottom' sx={{ zIndex: 9000000 }} open={Boolean( previewFile )} slotProps={{
                paper: { sx: { height: "calc(100% - 50px)" } }
            }} >
                <Box bgcolor={palette.cardBackground} flexGrow={1} display="flex" overflow="auto" flexDirection="column" borderRadius="20px 20px 0 0">
                    <Box borderBottom={border[1]} alignItems="center" display="flex" justifyContent="space-between" gap="20px" padding="10px 20px">
                        <Typography variant='h6'>{previewingFileDetails?.name}</Typography>
                        <Box display="flex" gap="10px" alignItems="center">
                            <IconButton onClick={() => { setPreviewFile( null ); setPreviewingFileDetails( null ) }}>
                                {Icons.default.CloseIcon}
                            </IconButton>
                        </Box>
                    </Box>
                    <Box display="flex" flexDirection="column" overflow="auto" flexGrow={1} padding="20px" height="300px">
                        <DocumentViewer fileDetails={previewingFileDetails} loadingText="Loading attachment..." setFile={setPreviewFile} type={previewingFileDetails?.type || previewingFileDetails?.name?.split( "." ).pop()} file={previewFile} />
                    </Box>
                </Box>
            </Drawer>}
            {!loading && !error && announcement && <Box display="flex" flexGrow={1} flexDirection="column" overflow="hidden">
                <Box position="relative" padding="20px" borderBottom={border[1]}>
                    {announcement?.cover_image && <Box sx={{ position: "absolute", inset: 0, opacity: 0.8, pointerEvents: "none", zIndex: "1" }}>
                        <img style={{ objectFit: "cover" }} height="100%" width="100%" src={convertToCDNLink( announcement.cover_image )} alt="Cover banner" />
                    </Box>}
                    {announcement?.cover_image && <Box sx={{ position: "absolute", inset: 0, background: `linear-gradient(to right,${palette.common.bg},transparent 250%)`, pointerEvents: "none", zIndex: "3" }}>
                    </Box>}
                    <Box position="relative" sx={{ zIndex: 10 }}>
                        <Typography marginTop="10px" variant='h4'>
                            {announcement.announcement_title}
                        </Typography>
                        <Typography display="flex" alignItems='flex-start' gap="20px" variant='subtitle2'>
                            <em>{`${formatDate( announcement.announcement_created_at )}`}</em>
                        </Typography>
                    </Box>
                </Box>
                <Box flexGrow={1} className="slim-custom-scrollbar" display={{ md: "grid", xs: "initial" }} flexDirection={{ md: "initial", xs: "column-reverse" }} overflow="auto" alignItems="flex-start" gridTemplateColumns={{ md: announcement.attachments && announcement.attachments?.length > 0 ? "auto 320px" : "1fr", xs: "1fr" }}>
                    <Box padding="0 20px" overflow="auto" className="slim-custom-scrollbar" height={{ md: "100%", xs: "initial" }}>
                        <Box className="wysiwyg-text-container" marginTop="10px" dangerouslySetInnerHTML={{ __html: announcement.announcement_message }}></Box>
                    </Box>
                    {announcement.attachments && announcement.attachments?.length > 0 && <Box bgcolor={{ md: palette.contentBg, xs: "none" }} position={{ md: "initial", xs: "absolute" }} bottom={0} left="0" right="5px" display="flex" className="slim-custom-scrollbar" height={{ md: "100%", xs: "initial" }} overflow="auto" alignContent="flex-start" alignItems="flex-start" gap="20px" flexWrap="wrap" >
                        <Box marginX={{ md: "0", xs: "10px" }} flexGrow={1}>
                            <Box border={border[1]} borderBottom={showAttachmentsOnSmallScreen ? border[1] : "none"} bgcolor={palette.contentBg} sx={{ cursor: "pointer" }} borderRadius="10px 10px 0 0" overflow="clip" padding="10px" onClick={() => setShowAttachmentsOnSmallScreen( prev => !prev )} display={{ md: "none", xs: "flex" }} alignItems="center" justifyContent="space-between" gap="20px">
                                <Box flexGrow={1} display="flex" alignItems="center" gap="20px">
                                    <AttachmentOutlined fontSize='small' />
                                    <Typography flexGrow={1} variant='subtitle2'>View Attachments</Typography>
                                </Box>
                                {!showAttachmentsOnSmallScreen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            </Box>
                            <Box bgcolor={palette.contentBg} borderRight={{ md: "none", xs: showAttachmentsOnSmallScreen ? border[1] : "none" }} borderLeft={{ md: "none", xs: showAttachmentsOnSmallScreen ? border[1] : "none" }} justifyContent={{ md: "flex-end", xs: "flex-start" }} className="slim-custom-scrollbar" overflow={{ md: "initial", xs: "auto" }} maxHeight={{ md: "initial", xs: "200px" }} display={{ md: "flex", xs: showAttachmentsOnSmallScreen ? "flex" : "none" }} padding={{ xs: "10px", md: "20px" }} flexWrap="wrap" alignItems="center" gap={{ md: "20px", xs: "10px" }} >
                                {announcement.attachments?.length > 0 && announcement.attachments?.map( ( attachment, attachmentIndex ) => {
                                    return (
                                        <AttachmentChip announcement={announcement} setPreviewFile={setPreviewFile} setPreviewingFileDetails={setPreviewingFileDetails} key={`attachment-${attachmentIndex}`} attachment={attachment} />
                                    )
                                } )}
                            </Box>
                        </Box>
                    </Box>}
                </Box>
            </Box>}
            {error && <Box display="flex" flexGrow={1} flexDirection="column" alignItems="center">
                <TryAgainMessageBlock err={error.message} styles={{ flexGrow: 1 }} code={error.code} getData={fetchAnnouncement} loading={loading} />
            </Box>}
            {!error && loading && <DataLoadingSpinner waitingMessage="Loading announcement details..." />}
        </Dialog >
    )
}

export default AnnouncementDialog
